/**
 * JsonDataViewer Component
 *
 * Collapsible JSON display with syntax highlighting.
 * Used in Expert View to show full input/output data at each step.
 */

import { useState } from 'react';
import { clsx } from 'clsx';
import { ChevronRight, ChevronDown, Copy, Check } from 'lucide-react';

interface JsonDataViewerProps {
  data: unknown;
  title?: string;
  maxHeight?: number;
  defaultExpanded?: boolean;
  searchable?: boolean;
}

interface CollapsibleNodeProps {
  label: string;
  value: unknown;
  depth: number;
  defaultExpanded?: boolean;
}

function getTypeColor(value: unknown): string {
  if (value === null) return 'text-gray-400';
  if (typeof value === 'string') return 'text-emerald-600';
  if (typeof value === 'number') return 'text-blue-600';
  if (typeof value === 'boolean') return 'text-purple-600';
  return 'text-gray-700';
}

function formatPrimitive(value: unknown): string {
  if (value === null) return 'null';
  if (typeof value === 'string') return `"${value}"`;
  if (typeof value === 'undefined') return 'undefined';
  return String(value);
}

function CollapsibleNode({ label, value, depth, defaultExpanded = false }: CollapsibleNodeProps) {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded || depth < 2);

  const isObject = value !== null && typeof value === 'object';
  const isArray = Array.isArray(value);
  const isEmpty = isObject && Object.keys(value).length === 0;

  // Indent based on depth
  const paddingLeft = depth * 16;

  if (!isObject || isEmpty) {
    return (
      <div className="flex items-start py-0.5" style={{ paddingLeft }}>
        <span className="text-gray-500 mr-1">{label}:</span>
        <span className={getTypeColor(value)}>
          {isEmpty ? (isArray ? '[]' : '{}') : formatPrimitive(value)}
        </span>
      </div>
    );
  }

  const entries = Object.entries(value);
  const previewText = isArray
    ? `Array(${entries.length})`
    : `Object(${entries.length})`;

  return (
    <div>
      <button
        className="flex items-center py-0.5 hover:bg-gray-50 w-full text-left"
        style={{ paddingLeft }}
        onClick={() => setIsExpanded(!isExpanded)}
      >
        {isExpanded ? (
          <ChevronDown className="h-3.5 w-3.5 text-gray-400 mr-1 flex-shrink-0" />
        ) : (
          <ChevronRight className="h-3.5 w-3.5 text-gray-400 mr-1 flex-shrink-0" />
        )}
        <span className="text-gray-500 mr-1">{label}:</span>
        {!isExpanded && (
          <span className="text-gray-400 text-xs">{previewText}</span>
        )}
      </button>

      {isExpanded && (
        <div>
          {entries.map(([key, val]) => (
            <CollapsibleNode
              key={key}
              label={isArray ? `[${key}]` : key}
              value={val}
              depth={depth + 1}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export function JsonDataViewer({
  data,
  title,
  maxHeight = 400,
  defaultExpanded = true,
}: JsonDataViewerProps) {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const [copied, setCopied] = useState(false);

  const handleCopy = async (e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(JSON.stringify(data, null, 2));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  return (
    <div className="border border-gray-200 rounded-lg bg-gray-50 overflow-hidden">
      {/* Header */}
      <div
        className={clsx(
          'flex items-center justify-between px-3 py-2 bg-gray-100 border-b border-gray-200 cursor-pointer',
          'hover:bg-gray-150'
        )}
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center gap-2">
          {isExpanded ? (
            <ChevronDown className="h-4 w-4 text-gray-500" />
          ) : (
            <ChevronRight className="h-4 w-4 text-gray-500" />
          )}
          {title && <span className="text-sm font-medium text-gray-700">{title}</span>}
          {!isExpanded && (
            <span className="text-xs text-gray-400">
              {Array.isArray(data)
                ? `[${data.length} items]`
                : typeof data === 'object' && data
                  ? `{${Object.keys(data).length} keys}`
                  : typeof data}
            </span>
          )}
        </div>

        <button
          onClick={handleCopy}
          className={clsx(
            'p-1 rounded hover:bg-gray-200 transition-colors',
            copied ? 'text-green-600' : 'text-gray-500'
          )}
          title="Copy JSON"
        >
          {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
        </button>
      </div>

      {/* Content */}
      {isExpanded && (
        <div
          className="p-3 font-mono text-xs overflow-auto"
          style={{ maxHeight }}
        >
          {data !== null && typeof data === 'object' ? (
            Object.entries(data).map(([key, value]) => (
              <CollapsibleNode
                key={key}
                label={Array.isArray(data) ? `[${key}]` : key}
                value={value}
                depth={0}
              />
            ))
          ) : (
            <span className={getTypeColor(data)}>{formatPrimitive(data)}</span>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Compact JSON preview for use in node headers
 */
export function JsonPreview({ data, maxLength = 80 }: { data: unknown; maxLength?: number }) {
  const preview = JSON.stringify(data);
  const truncated = preview.length > maxLength
    ? preview.substring(0, maxLength) + '...'
    : preview;

  return (
    <span className="font-mono text-xs text-gray-500 truncate">
      {truncated}
    </span>
  );
}
